import React, { useEffect, useRef, useState } from 'react';
import { FiMoreVertical, FiEye, FiEdit, FiPrinter, FiArchive, FiTrash2 } from "react-icons/fi";
import ReactDOM from 'react-dom';
import ViewDetailVaccination from '../Popup/ViewDetailVaccination';
import EditVaccination from '../Popup/EditVaccination';
import VaccinationService from '../services/vaccinationService';

const ActionTableButton = ({ 
  item, 
  refreshData,
  viewModalTarget = "viewVaccinationDetailModal" 
}) => {
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  
  // States
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showModal, setShowModal] = useState(false);
  const [selectedVaccination, setSelectedVaccination] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [vaccinations, setVaccinations] = useState([]);
  
  // Function to update a specific item locally
  const updateItemLocally = (id, updatedFields) => {
    setVaccinations(prevVaccinations => 
      prevVaccinations.map(vaccination => 
        (vaccination.id === id || vaccination._id === id) 
          ? { ...vaccination, ...updatedFields } 
          : vaccination
      )
    );
  };

  // Determine if the vaccination is active or not
  const isActive = item.isActive !== undefined ? item.isActive : true;

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: buttonRect.bottom + scrollTop,
      left: buttonRect.right - 150 + scrollLeft, // 150px is the approximate width of the dropdown
    };
  };
  
  // Handle action button click
  const toggleDropdown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Calculate and set position before showing
    const position = calculatePosition();
    setDropdownPosition(position);
    setDropdownVisible(!dropdownVisible);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownVisible && 
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);
  
  // Effect to handle scroll lock when modal is open
  useEffect(() => {
    if (showModal) {
      // Prevent scroll on body
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px'; // Compensate for scrollbar
    } else {
      // Restore scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    
    return () => {
      // Cleanup when unmounting
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showModal]);
  
  // Action functions
  const handleViewDetails = (e) => {
    e.stopPropagation();
    console.log("View details for vaccination:", item.id || item._id);
    setSelectedVaccination(item);
    setIsEditMode(false);
    setShowModal(true);
    setDropdownVisible(false);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setSelectedVaccination(null);
    setIsEditMode(false);
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    console.log('Edit vaccination with ID:', item.id || item._id);
    // Prepare vaccination object with a signal to indicate edit mode
    const vaccinationWithEditMode = { ...item, initialEditMode: true };
    setSelectedVaccination(vaccinationWithEditMode);
    setIsEditMode(true);
    setShowModal(true);
    setDropdownVisible(false);
  };
  
  const handlePrint = (e) => {
    e.stopPropagation();
    console.log('Print vaccination with ID:', item.id || item._id);
    
    // Create a new window for printing
    const printWindow = window.open('', '', 'width=800,height=600');
    
    // Create HTML content for printing with all available data
    printWindow.document.write(`
      <html>
        <head>
          <title>Vaccination Record</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px;
            }
            .print-header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            h1 { 
              margin-bottom: 5px;
            }
            .vaccination-id {
              font-size: 14px;
              color: #666;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold; 
            }
            .patient-doctor-info {
              display: flex;
              justify-content: space-between;
            }
            .patient-info, .doctor-info {
              width: 48%;
            }
            .footer {
              margin-top: 40px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              text-align: center;
              color: #666;
            }
            @media print {
              .page-break {
                page-break-before: always;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Vaccination Record</h1>
            <div class="vaccination-id">Vaccination ID: ${item.id || item._id}</div>
            <div>Date: ${item.dateAdministered || item.vaccinationDate || new Date().toLocaleDateString()}</div>
          </div>
          
          <div class="patient-doctor-info">
            <div class="patient-info section">
              <div class="section-title">Patient Information</div>
              <p><strong>Name:</strong> ${item.patient?.name || item.patientName || "N/A"}</p>
              <p><strong>ID:</strong> ${item.patient?._id || item.patientId || "N/A"}</p>
              <p><strong>Age:</strong> ${item.patient?.age || item.patientAge || "N/A"}</p>
              <p><strong>Gender:</strong> ${item.patient?.gender || item.patientGender || "N/A"}</p>
            </div>
            
            <div class="doctor-info section">
              <div class="section-title">Healthcare Provider</div>
              <p><strong>Name:</strong> ${item.doctor?.name || item.doctorName || item.healthcareProvider || "N/A"}</p>
              <p><strong>ID:</strong> ${item.doctor?._id || item.doctorId || item.providerId || "N/A"}</p>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Vaccination Information</div>
            <table>
              <thead>
                <tr>
                  <th>Vaccine</th>
                  <th>Manufacturer</th>
                  <th>Lot Number</th>
                  <th>Dose</th>
                  <th>Site</th>
                  <th>Route</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${item.vaccine?.name || item.vaccineName || "N/A"}</td>
                  <td>${item.vaccine?.manufacturer || item.manufacturer || "N/A"}</td>
                  <td>${item.lotNumber || item.batchNumber || "N/A"}</td>
                  <td>${item.dose || item.dosage || "N/A"}</td>
                  <td>${item.site || item.injectionSite || "N/A"}</td>
                  <td>${item.route || item.administrationRoute || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Additional Information</div>
            <p><strong>Date Administered:</strong> ${item.dateAdministered || item.vaccinationDate || "N/A"}</p>
            <p><strong>Next Due Date:</strong> ${item.nextDueDate || "N/A"}</p>
            <p><strong>Status:</strong> ${item.isActive ? "Active" : "Inactive"}</p>
            <p><strong>Notes:</strong> ${item.notes || item.observations || "None"}</p>
          </div>
          
          ${item.adverseReactions && item.adverseReactions.length > 0 ? `
            <div class="section">
              <div class="section-title">Adverse Reactions</div>
              <table>
                <thead>
                  <tr>
                    <th>Reaction</th>
                    <th>Severity</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  ${item.adverseReactions.map(reaction => `
                    <tr>
                      <td>${reaction.reaction || reaction}</td>
                      <td>${reaction.severity || "N/A"}</td>
                      <td>${reaction.date || "N/A"}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>This vaccination record was generated on ${new Date().toLocaleString()}</p>
            <p>Please keep this record for future reference and medical appointments.</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setDropdownVisible(false);
  };
  
  // Keep the original delete function that's working
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this vaccination?`)) {
      try {
        const vaccinationId = item.id || item._id;
        console.log('Delete vaccination with ID:', vaccinationId);
        
        // Call the delete API
        await VaccinationService.deleteVaccination(vaccinationId);
        
        alert('Vaccination deleted successfully');
        
        // If there's a refreshData function, call it with the deleted item
        if (refreshData) {
          refreshData(item);
          window.location.reload(); // Pasar el elemento eliminado
        } else {
          // If no refreshData function is provided, refresh the page
          window.location.reload();
        }
      } catch (error) {
        console.error('Error deleting vaccination:', error);
        alert('Failed to delete vaccination. Please try again.');
      }
      
      setDropdownVisible(false);
    }
  };
  
  const handleUpdateSuccess = async (updatedVaccination) => {
    try {
      console.log('Preparing to update vaccination:', updatedVaccination);
      
      // Format the data according to what backend expects
      const formattedData = {
        _id: updatedVaccination._id,
        patient: updatedVaccination.patient?._id || updatedVaccination.patient,
        doctor: updatedVaccination.doctor?._id || updatedVaccination.doctor,
        vaccine: updatedVaccination.vaccine?._id || updatedVaccination.vaccine,
        dateAdministered: updatedVaccination.dateAdministered || updatedVaccination.vaccinationDate,
        dose: updatedVaccination.dose || updatedVaccination.dosage,
        site: updatedVaccination.site || updatedVaccination.injectionSite,
        route: updatedVaccination.route || updatedVaccination.administrationRoute,
        lotNumber: updatedVaccination.lotNumber || updatedVaccination.batchNumber,
        manufacturer: updatedVaccination.manufacturer,
        nextDueDate: updatedVaccination.nextDueDate,
        notes: updatedVaccination.notes || updatedVaccination.observations,
        adverseReactions: Array.isArray(updatedVaccination.adverseReactions) 
          ? updatedVaccination.adverseReactions 
          : [],
        isActive: updatedVaccination.isActive
      };
      
      console.log('Sending formatted data to API:', formattedData);
      
      // Use the service to update the vaccination
      const vaccinationId = updatedVaccination._id || updatedVaccination.id;
      const response = await VaccinationService.updateVaccination(vaccinationId, formattedData);
      
      console.log('Update response:', response);
      
      if (response && response.status) {
        alert('Vaccination updated successfully');
        if (refreshData) refreshData();
        closeModal();
      } else {
        throw new Error(response?.message || 'Failed to update vaccination');
      }
    } catch (error) {
      console.error('Error updating vaccination:', error);
      alert(`Update failed: ${error.message}`);
    }
  };
  
  const handleCancel = () => {
    // Handle cancellation
    console.log('Edit cancelled');
    // Close the modal
    closeModal();
  };
  
  // Dropdown component that will render in portal
  const DropdownMenu = () => {
    if (!dropdownVisible) return null;
    
    return ReactDOM.createPortal(
      <div 
        ref={dropdownRef}
        className="dropdown-menu show"
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          zIndex: 9999,
          minWidth: '150px',
          backgroundColor: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,.2)',
          border: '1px solid rgba(0,0,0,.15)',
          borderRadius: '4px',
          padding: '0.5rem 0'
        }}
      >
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleViewDetails}
        >
          <FiEye className="me-2" /> View Details
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleEdit}
        >
          <FiEdit className="me-2" /> Edit
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handlePrint}
        >
          <FiPrinter className="me-2" /> Print
        </button>        
        <button 
          className="dropdown-item d-flex align-items-center text-danger" 
          type="button"
          onClick={handleDelete}
        >
          <FiTrash2 className="me-2" /> Delete
        </button>
      </div>,
      document.body
    );
  };
  
  // Modal component for viewing details
  const DetailModal = () => {
    if (!showModal) return null;
    
    // Using portal to render the modal outside the normal DOM flow
    return ReactDOM.createPortal(
      <div className="modal-backdrop-custom">
        <div 
          ref={modalRef}
          className="modal-custom" 
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="modal-dialog modal-lg" 
            style={{
              maxWidth: '800px',
              width: '100%',
              margin: '10px',
              backgroundColor: '#fff',
              borderRadius: '5px',
              boxShadow: '0 5px 15px rgba(0,0,0,.5)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              overflow: 'hidden'
            }}>
              <div className="modal-header" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderBottom: '1px solid #dee2e6'
              }}>
                <h5 className="modal-title">
                  {isEditMode ? 'Edit Vaccination' : 'Vaccination Details'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{
                padding: '1rem',
                overflow: 'auto'
              }}>
                {selectedVaccination && !isEditMode && ( 
                  <ViewDetailVaccination 
                    vaccination={selectedVaccination} 
                  />
                )}
                {selectedVaccination && isEditMode && (
                  <EditVaccination 
                    vaccination={selectedVaccination} 
                    onUpdateSuccess={handleUpdateSuccess}
                    onCancel={handleCancel}
                  />
                )}
              </div>
              <div className="modal-footer" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '1rem',
                borderTop: '1px solid #dee2e6'
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <div onClick={e => e.stopPropagation()}>
        <button 
          ref={buttonRef}
          className="btn btn-sm" 
          type="button" 
          onClick={toggleDropdown}
          aria-expanded={dropdownVisible}
        >
          <FiMoreVertical />
        </button>
      </div>
      
      {/* Render dropdown menu through portal */}
      <DropdownMenu />
      
      {/* Render modal through portal */}
      <DetailModal />
    </>
  );
};

export default ActionTableButton;